/**
 * Conexiones de la cuenta (Facebook/Instagram vía Facebook Login for Business) y publicaciones que no se
 * pudieron borrar. Mismo patrón que `products`: se muestra lo último conocido mientras se refresca.
 */
import { defineStore } from "pinia";
import { computed, shallowRef } from "vue";

import { api } from "@/lib/api";
import type { ConnectionsResponse, Destination, MetaConnection } from "@/lib/contracts";

export const useConnectionsStore = defineStore("connections", () => {
  const data = shallowRef<ConnectionsResponse | null>(null);

  const meta = computed<MetaConnection | null>(() => data.value?.meta ?? null);
  const orphans = computed(() => data.value?.orphans ?? []);
  /** Avisos para la biblioteca: hay que reconectar o hay publicaciones por borrar a mano. */
  const needsAttention = computed(() => meta.value?.status === "needs_reauth" || orphans.value.length > 0);

  async function refresh(): Promise<ConnectionsResponse> {
    const res = await api.getConnections();
    // Un backend sin /connections (o una respuesta no JSON) no debe romper la biblioteca.
    data.value = {
      meta: res.meta ?? null,
      meta_login_available: res.meta_login_available ?? false,
      destinations: res.destinations ?? {},
      orphans: Array.isArray(res.orphans) ? res.orphans : [],
    };
    return data.value;
  }

  function setMeta(next: MetaConnection): void {
    if (data.value) data.value = { ...data.value, meta: next };
  }

  function dropOrphan(productId: string, destination: Destination): void {
    if (!data.value) return;
    data.value = {
      ...data.value,
      orphans: data.value.orphans.filter((o) => !(o.product_id === productId && o.destination === destination)),
    };
  }

  function reset(): void {
    data.value = null;
  }

  return { data, meta, orphans, needsAttention, refresh, setMeta, dropOrphan, reset };
});
